import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Loader2, Sparkles, ArrowUp, FileText, ListChecks, History, MessageSquarePlus } from 'lucide-react';
import { cn } from './ui/utils';
import { invokeEdgeFn } from '@/lib/edge-functions';
import { supabase } from '@saas-boilerplate/shared';

type Citation = { kind: 'meeting' | 'action_item'; id: string; full_id?: string; label?: string };
type Message = {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  citations?: Citation[];
  intent?: string;
};
type AgentResponse = {
  conversation_id: string;
  message_id?: string | null;
  role: 'assistant';
  content: string;
  citations: Citation[];
  intent?: string;
  error?: string;
};

const AgentOpenerContext = createContext<() => void>(() => {});
export function useAgentChatOpener() {
  return useContext(AgentOpenerContext);
}

export function AgentChatProvider({
  children,
  workspaceId,
  onNavigateToMeeting,
}: {
  children: ReactNode;
  workspaceId: string | null | undefined;
  onNavigateToMeeting?: (meetingId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const opener = useCallback(() => setOpen(true), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <AgentOpenerContext.Provider value={opener}>
      {children}
      <AgentChatDialog
        open={open}
        onOpenChange={setOpen}
        workspaceId={workspaceId}
        onNavigateToMeeting={onNavigateToMeeting}
      />
    </AgentOpenerContext.Provider>
  );
}

function AgentChatDialog({
  open,
  onOpenChange,
  workspaceId,
  onNavigateToMeeting,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  workspaceId: string | null | undefined;
  onNavigateToMeeting?: (meetingId: string) => void;
}) {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyList, setHistoryList] = useState<Array<{ id: string; title: string | null; updated_at: string }>>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!workspaceId) return;
    setHistoryLoading(true);
    try {
      const { data } = await supabase
        .from('agent_conversations')
        .select('id, title, updated_at')
        .eq('workspace_id', workspaceId)
        .is('archived_at', null)
        .order('updated_at', { ascending: false })
        .limit(30);
      setHistoryList(
        ((data as unknown[]) || []).map((r) => ({
          id: String((r as any).id),
          title: ((r as any).title as string | null) || null,
          updated_at: String((r as any).updated_at),
        })),
      );
    } catch {
      setHistoryList([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [workspaceId]);

  const openConversation = useCallback(async (cid: string) => {
    setHistoryOpen(false);
    setLoadingConversation(true);
    setConversationId(cid);
    try {
      const { data } = await supabase
        .from('agent_messages')
        .select('id, role, content, citations, intent, created_at')
        .eq('conversation_id', cid)
        .order('created_at', { ascending: true })
        .limit(200);
      const loaded: Message[] = ((data as unknown[]) || []).map((r) => ({
        id: String((r as any).id),
        role: ((r as any).role as 'user' | 'assistant'),
        content: String((r as any).content || ''),
        citations: ((r as any).citations as Citation[] | null) || [],
        intent: ((r as any).intent as { intent?: string } | null)?.intent,
      })).filter((m) => m.role === 'user' || m.role === 'assistant');
      setMessages(loaded);
    } catch {
      setMessages([]);
    } finally {
      setLoadingConversation(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, []);

  // Focus input on open + auto-scroll on new message + refresh history.
  useEffect(() => {
    if (!open) return;
    setTimeout(() => inputRef.current?.focus(), 0);
    void fetchHistory();
  }, [open, fetchHistory]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Reset state on close.
  const handleOpenChange = (v: boolean) => {
    onOpenChange(v);
    if (!v) {
      // Soft reset - keep conversation if we have one (persisted) but clear local input.
      setInput('');
    }
  };

  const startNewConversation = () => {
    setConversationId(null);
    setMessages([]);
    setInput('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending || !workspaceId) return;
    setInput('');
    setSending(true);
    const userMsg: Message = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    try {
      const res = await invokeEdgeFn<AgentResponse>('agent-chat', {
        conversation_id: conversationId,
        workspace_id: workspaceId,
        message: text,
      });
      if (res.conversation_id) setConversationId(res.conversation_id);
      setMessages((prev) => [
        ...prev,
        { id: res.message_id || undefined, role: 'assistant', content: res.content, citations: res.citations, intent: res.intent },
      ]);
      // Refresh history so the new conversation shows up at the top with its title.
      void fetchHistory();
    } catch (e: any) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Sorry - ${e?.message || 'something went wrong'}`, citations: [] },
      ]);
    } finally {
      setSending(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const onKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex h-[min(80vh,720px)] max-w-2xl flex-col gap-0 overflow-hidden p-0 sm:max-w-2xl"
      >
        <DialogTitle className="sr-only">Ask Upstand</DialogTitle>
        <div className="flex items-center justify-between border-b border-border/40 px-5 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <div>
              <div className="text-sm font-semibold text-foreground">Ask Upstand</div>
              <div className="text-[11px] text-muted-foreground">
                Beta · grounded in your meetings ·{' '}
                <kbd className="rounded border border-border/60 bg-muted/40 px-1 font-mono text-[10px]">⌘J</kbd>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
              <PopoverTrigger asChild>
                <Button type="button" variant="ghost" size="sm" className="h-8 gap-1.5 px-2 text-xs">
                  <History className="h-3.5 w-3.5" />
                  History
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72 p-0">
                <div className="border-b border-border/40 px-3 py-2">
                  <p className="text-xs font-semibold text-foreground">Recent conversations</p>
                </div>
                <div className="max-h-80 overflow-y-auto py-1">
                  {historyLoading ? (
                    <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" /> Loading…
                    </div>
                  ) : historyList.length === 0 ? (
                    <p className="px-3 py-4 text-xs italic text-muted-foreground">No previous chats yet.</p>
                  ) : (
                    historyList.map((c) => {
                      const active = c.id === conversationId;
                      const updated = (() => {
                        try {
                          return new Date(c.updated_at).toLocaleString('en-GB', {
                            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                          });
                        } catch { return ''; }
                      })();
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => void openConversation(c.id)}
                          className={cn(
                            'flex w-full flex-col items-start gap-0.5 border-b border-border/20 px-3 py-2 text-left text-xs transition-colors hover:bg-muted/40 last:border-0',
                            active && 'bg-primary/5',
                          )}
                        >
                          <span className={cn('line-clamp-1 text-foreground', active && 'font-medium text-primary')}>
                            {c.title || 'Untitled chat'}
                          </span>
                          <span className="text-[10px] text-muted-foreground">{updated}</span>
                        </button>
                      );
                    })
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={startNewConversation}
              className="h-8 gap-1.5 px-2 text-xs"
              aria-label="New chat"
            >
              <MessageSquarePlus className="h-3.5 w-3.5" />
              New
            </Button>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {loadingConversation ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading conversation…
            </div>
          ) : messages.length === 0 ? (
            <Suggestions onPick={(s) => setInput(s)} />
          ) : (
            messages.map((m, i) => (
              <MessageBubble key={m.id || `${m.role}-${i}`} message={m} onNavigateToMeeting={onNavigateToMeeting} />
            ))
          )}
          {sending && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Thinking…
            </div>
          )}
        </div>

        <div className="border-t border-border/40 p-3">
          <div className="flex items-end gap-2">
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ask about your meetings… e.g. catch me up on last week's standups"
              rows={2}
              disabled={sending}
              className="min-h-[44px] resize-none"
            />
            <Button
              type="button"
              size="icon"
              onClick={() => void send()}
              disabled={sending || !input.trim() || !workspaceId}
              className="h-10 w-10 shrink-0"
              aria-label="Send"
            >
              <ArrowUp className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-1.5 px-1 text-[10px] text-muted-foreground">
            Enter to send · Shift-Enter for newline. Only your own meetings are searched.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Suggestions({ onPick }: { onPick: (s: string) => void }) {
  const items = [
    'What did I miss in my last few meetings?',
    'Summarise my last meeting',
    'Show me my open action items',
    'How has [name] been participating recently?',
  ];
  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
      <Sparkles className="h-8 w-8 text-primary/70" />
      <div>
        <p className="text-sm font-medium text-foreground">Ask anything about your meetings</p>
        <p className="text-xs text-muted-foreground">
          Grounded in your meeting history - every answer cites the source.
        </p>
      </div>
      <div className="grid w-full max-w-md gap-2">
        {items.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onPick(s)}
            className="rounded-md border border-border/60 bg-card px-3 py-2 text-left text-sm text-foreground transition-colors hover:border-primary/40 hover:bg-muted/40"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({
  message,
  onNavigateToMeeting,
}: {
  message: Message;
  onNavigateToMeeting?: (meetingId: string) => void;
}) {
  const isUser = message.role === 'user';
  // Map prefix → full id so inline citations can deep-link.
  const meetingMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of message.citations || []) if (c.kind === 'meeting' && c.full_id) m.set(c.id, c.full_id);
    return m;
  }, [message.citations]);
  const actionMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of message.citations || []) if (c.kind === 'action_item' && c.full_id) m.set(c.id, c.full_id);
    return m;
  }, [message.citations]);

  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[88%] rounded-lg px-3.5 py-2.5 text-sm leading-relaxed',
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted/40 text-foreground',
        )}
      >
        <RenderedContent
          content={message.content}
          meetingMap={meetingMap}
          actionMap={actionMap}
          onNavigateToMeeting={onNavigateToMeeting}
          isUser={isUser}
        />
        {!isUser && (message.citations?.length ?? 0) > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5 border-t border-border/30 pt-2">
            {(message.citations || []).map((c) => (
              <CitationChip key={`${c.kind}:${c.id}`} citation={c} onNavigateToMeeting={onNavigateToMeeting} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CitationChip({
  citation,
  onNavigateToMeeting,
}: {
  citation: Citation;
  onNavigateToMeeting?: (meetingId: string) => void;
}) {
  if (citation.kind === 'meeting' && citation.full_id) {
    return (
      <button
        type="button"
        onClick={() => onNavigateToMeeting?.(citation.full_id!)}
        className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background px-2 py-0.5 text-[11px] font-medium text-primary transition-colors hover:border-primary/40 hover:bg-primary/5"
        title={citation.label || 'Open meeting'}
      >
        <FileText className="h-3 w-3" />
        {citation.label || 'Meeting'}
      </button>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background px-2 py-0.5 text-[11px] font-medium text-muted-foreground"
      title={citation.label || 'Action item'}
    >
      <ListChecks className="h-3 w-3" />
      Action
    </span>
  );
}

/** Replace [mtg_XXXXXXXX] / [aid:XXXXXXXX] inline tokens with clickable chips. */
function RenderedContent({
  content,
  meetingMap,
  actionMap,
  onNavigateToMeeting,
  isUser,
}: {
  content: string;
  meetingMap: Map<string, string>;
  actionMap: Map<string, string>;
  onNavigateToMeeting?: (meetingId: string) => void;
  isUser: boolean;
}) {
  if (isUser) return <span className="whitespace-pre-wrap">{content}</span>;
  const parts: Array<{ kind: 'text' | 'mtg' | 'aid'; value: string }> = [];
  const re = /\[(mtg_[a-f0-9]{8}|aid:[a-f0-9]{8})(?:\s*,\s*(?:mtg_[a-f0-9]{8}|aid:[a-f0-9]{8}))*\]/g;
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content))) {
    if (m.index > lastIdx) parts.push({ kind: 'text', value: content.slice(lastIdx, m.index) });
    const inner = m[0].slice(1, -1);
    const tokens = inner.split(/\s*,\s*/);
    for (const tok of tokens) {
      if (tok.startsWith('mtg_')) parts.push({ kind: 'mtg', value: tok.slice(4) });
      else if (tok.startsWith('aid:')) parts.push({ kind: 'aid', value: tok.slice(4) });
    }
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < content.length) parts.push({ kind: 'text', value: content.slice(lastIdx) });

  return (
    <span className="whitespace-pre-wrap">
      {parts.map((p, i) => {
        if (p.kind === 'text') return <span key={i}>{p.value}</span>;
        if (p.kind === 'mtg' && meetingMap.has(p.value)) {
          const fullId = meetingMap.get(p.value)!;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onNavigateToMeeting?.(fullId)}
              className="mx-0.5 inline-flex items-center gap-0.5 rounded border border-primary/30 bg-primary/5 px-1 py-0 text-[10px] font-semibold uppercase tracking-wider text-primary transition-colors hover:bg-primary/10"
            >
              <FileText className="h-2.5 w-2.5" />
              src
            </button>
          );
        }
        if (p.kind === 'aid' && actionMap.has(p.value)) {
          return (
            <span
              key={i}
              className="mx-0.5 inline-flex items-center gap-0.5 rounded border border-border/60 bg-background px-1 py-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
            >
              <ListChecks className="h-2.5 w-2.5" />
              src
            </span>
          );
        }
        return <span key={i} className="text-muted-foreground/70">{p.kind === 'mtg' ? `[mtg_${p.value}]` : `[aid:${p.value}]`}</span>;
      })}
    </span>
  );
}
